import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  ActivityIndicator,
  Keyboard,
  Modal,
  Platform,
  ReturnKeyTypeOptions,
  TextInput,
  TextProps,
  TouchableHighlight,
  TouchableHighlightProps,
  View
} from 'react-native';
import styled from 'styled-components/native';
import Colors from '../../../constants/Colors';
import { useDynamicFormContext } from '../../DynamicFormContext';
import { FormElementProps } from '../../FormElement';
import { FormElementPlugin } from '../FormElementPlugin';
import { FormInputVariant } from '../../../components/form/FormInput';
import {
  internationalPhoneMask,
  InternationalPhoneNumber
} from '../../../phone-number/InternationalPhoneNumberMask';
import { useHttpFetchApi } from '../../../hooks/http/useHttpFetch';
import { TextInputMask, TextInputMaskProps } from 'react-native-masked-text';
import { Feather } from '@expo/vector-icons';
import SelectField, {
  SelectOption
} from '../../../components/form/SelectField';
import FlatListPicker from '../../../components/FlatlistPicker';
import { B, Caption } from '../../../components/Typography';
import FormElementQuestion from './components/FormElementQuestion';
import Spacer from '../../../components/Spacer';

const phoneRegExp =
  /^((\+\d{1,3}(-| )?\(?\d\)?(-| )?\d{1,3})|(\(?\d{2,3}\)?))(-| )?(\d{3,4})(-| )?(\d{4})(( x| ext)\d{1,5}){0,1}$/;

const FormInputView = styled.View`
  margin-top: 8px;
  width: 100%;
`;

const StyledTextInputView = styled.View<{
  error?: boolean;
}>`
  background-color: #fff;
  border: 1px solid ${p => (p.error ? Colors.error : Colors.formInputBorder)};
  border-radius: 8px;
  min-height: 66px;
  margin-bottom: ${p => (p.error ? 4 : 12)}px;
  flex-direction: row;
  align-items: center;
`;

const StyledTextInput = styled(TextInputMask)<{
  minHeight?: number;
  variant?: FormInputVariant;
  multiLine?: boolean;
  empty?: boolean;
}>`
  padding: ${p =>
    p.variant === 'survey' && (Platform.OS === 'ios' || Platform.OS === 'web')
      ? '20px 16px'
      : '20px 12px'};
  font-family: ${p => (p.empty ? 'WorkSans-Regular' : 'DMSans-Bold')};
  ${p => (p.variant === 'survey' ? `font-size: 15px;` : `font-size: 18px;`)}
  min-height: ${p => (p.minHeight ? `${p.minHeight}px` : 'auto')};
  background-color: transparent;
  flex: 1;
  padding-right: 8px;
` as React.FC<
  TextInputMaskProps & {
    minHeight?: number;
    variant?: FormInputVariant;
    multiLine?: boolean;
    refInput: any;
    empty?: boolean;
  }
>;

const StyledModalButton = styled.TouchableOpacity`
  height: 20px;
  margin-right: 16px;
`;

const HelperText = styled.Text`
  font-family: 'DMSans-Medium';
  font-size: 12px;
  line-height: 18px;
  color: ${Colors.formLabel};
  margin: 4px 4px 12px 4px;
`;

const ErrorText = styled(HelperText)<{ errorTextColor: string }>`
  color: ${p =>
    p.errorTextColor === 'dark' ? Colors.error : Colors.errorCaption};
`;

const StyledValidateView = styled.View`
  padding: 0 12px;
`;

const StyledTouchableHighlight = styled.TouchableHighlight<{
  variant?: FormInputVariant;
}>`
  position: relative;
  background-color: ${p =>
    p.variant === 'survey'
      ? Colors.primary
      : p.variant === 'inline'
        ? 'rgba(255, 255, 255, 0)'
        : '#fff'};
  border-radius: ${p => (p.variant === 'survey' ? 26.5 : 8)}px;
  padding: ${p => (p.variant === 'inline' ? '3' : '16')}px;
  /* color: #B3B3B3 */
  height: 64px;
  justify-content: center;
` as React.FC<
  TouchableHighlightProps & {
    ref: any;
    variant?: FormInputVariant;
  }
>;

const StyledText = styled.Text<{
  value?: string;
  variant?: FormInputVariant;
  empty?: boolean;
}>`
  color: ${p =>
    p.value
      ? p.variant === 'survey'
        ? '#fff'
        : p.variant === 'inline'
          ? `${Colors.primaryPurple}`
          : '#000'
      : '#bbbb'};
  font-size: ${p => (p.variant === 'inline' ? '16' : '18')}px;
  font-weight: ${p => (p.variant === 'inline' ? 'bold' : 'normal')};
  padding-right: 32px;

  padding-left: ${p => (p.variant === 'survey' ? 5 : 0)}px;
  font-family: ${p => (p.empty ? 'WorkSans-Regular' : 'DMSans-Bold')};
  /* white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis; */
` as React.FC<
  TextProps & {
    value?: string;
    variant?: FormInputVariant;
    empty?: boolean;
  }
>;

const StyledDropDownIcon = styled.View<{ variant?: FormInputVariant }>`
  position: absolute;
  align-self: center;
  right: 0px;
`;

const StyledModalView = styled.View`
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
`;

const StyledRowView = styled.View`
  flex-direction: row;
  flex-wrap: wrap;
`;

const StyledCheckboxContainer = styled.View<{ disabled?: boolean }>`
  flex-direction: row;
  opacity: ${p => (p.disabled ? 0.6 : 1)};
  align-items: center;
`;

const StyledTextContainer = styled.View`
  flex-shrink: 1;
`;

const StyledCaption = styled(Caption)`
  margin-right: 8px;
  font-size: ${p => (Platform.OS === 'web' ? 16 : 13)}px;
`;

const StyledBox = styled.TouchableOpacity<{
  touched?: boolean;
  dark?: boolean;
}>`
  align-self: flex-start;
  width: 18px;
  height: 18px;
  border-radius: 4px;
  /* border: 1px solid
    ${p =>
    p.touched ? Colors.formInputBorderHighlight : Colors.formInputBorder}; */
  background-color: ${p =>
    p.touched ? Colors.checkboxTouched : Colors.checkbox};
  ${p => (p.dark ? 'border-color: #000' : '')}
  align-items: center;
  justify-content: center;
  margin-right: 8px;
`;

const StyledModalBackgroundContainer = styled.TouchableWithoutFeedback`
  width: 100%;
  height: 100%;
  position: absolute;
`;

const StyledModalBackground = styled.View`
  width: 100%;
  height: 100%;
  position: absolute;
  background-color: black;
  opacity: 0.3;
`;

const StyledCountryPickerView = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: ${Platform.OS === 'web' ? '14px' : '0'};
`;

const StyledPickerView = styled.View`
  background-color: white;
  width: 100%;
  padding: ${Platform.OS === 'web' ? '24px 10px 10px 10px' : '10px'};
  max-width: ${Platform.OS === 'web' ? '308px' : '280px'};
  border-radius: 8px;
`;

const StyledModalButtonView = styled.View`
  flex-direction: row;
  width: 100%;
  align-items: center;
  justify-content: flex-end;
  margin-top: 32px;
  padding-bottom: 4px;
`;

export type FormPhoneNumberConfig = {
  placeholder?: string;
  multiline?: boolean;
};

export type FormPhoneNumberProps<FormValuesType> = FormElementProps<
  FormValuesType,
  FormPhoneNumberConfig
> & {
  select?: boolean;
  returnKeyType?: ReturnKeyTypeOptions;
  submitOnReturn?: boolean;
  variant?: FormInputVariant;
  dismissKeyboard?: boolean;
  errorTextColor?: 'dark' | 'light';
  mask?: string;
  values?: any;
  onChangeText?: (text: string) => void;
  onChange?: (
    event:
      | React.MouseEvent<HTMLButtonElement, MouseEvent>
      | React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
    value: FormValuesType[keyof FormValuesType]
  ) => void;
};

export const FormPhoneNumber = <FormValuesType extends object>({
  element,
  edit,
  disabled,
  select,
  children,
  onChange,
  returnKeyType,
  submitOnReturn,
  variant = 'outlined',
  dismissKeyboard,
  errorTextColor = 'light',
  onChangeText = (text: string) => ({}),
  ...props
}: PropsWithChildren<FormPhoneNumberProps<FormValuesType>>) => {
  const key = 'phoneNumber' as keyof FormValuesType;
  const phoneNumberCode = 'phoneNumberCode' as keyof FormValuesType;
  const notifyForMessages = 'notifyForMessages' as keyof FormValuesType;
  const notifyForStudy = 'notifyForStudy' as keyof FormValuesType;
  if (!element?.dataSource) element.dataSource = 'phoneNumber';
  const { formValues, setFormValue } = useDynamicFormContext<
    FormValuesType,
    FormPhoneNumberConfig
  >();
  const textInputRef = useRef<TextInput>(null!);
  const touchableHighlightRef = useRef<typeof TouchableHighlight>(null!);
  const [openMenu, setOpenMenu] = useState(false);
  const [height, setHeight] = useState<number>();
  const [touched, setTouched] = useState(false);
  const [values, setValues] = useState(
    props.values || {
      ...formValues[key],
      ...formValues[phoneNumberCode],
      notifyForMessages: formValues[notifyForMessages] ?? true,
      notifyForStudy: formValues[notifyForStudy] ?? true
    }
  );
  const [isPhoneNumberValid, setIsPhoneNumberValid] = useState(
    values[key]?.match(phoneRegExp) || false
  );
  const error = touched && isPhoneNumberValid;
  const [phoneNumberMask, setPhoneNumberMask] =
    useState<InternationalPhoneNumber>({
      code: 'US',
      dial_code: '+1',
      mask: '+1 (999) 999 9999',
      name: 'United States'
    });
  const [twilioError, setTwilioError] = useState<string>();
  const { loading, error: phoneValidationError, postJson } = useHttpFetchApi();
  const [initPhone, setInitPhone] = useState<boolean>(false);
  const [countryNumber, setCountryNumber] = useState<string>('');
  const [selectedCountryInfo, setSelectedCountryInfo] = useState<string>('');

  const handleTwilioLookup = useCallback(
    async (phoneNumber: string) => {
      const result = await postJson<{
        valid: boolean;
        validationErrors: string[];
      }>('twilio/lookup', { phoneNumber: phoneNumber || '' });
      setTwilioError(result?.valid ? undefined : `Not a valid phone number`);
      if (result?.valid) {
        setIsPhoneNumberValid(true);
        setFormValue(key, phoneNumber as any, element);
        setFormValue(
          phoneNumberCode,
          selectedCountryInfo.split(' ')[0] as any,
          element
        );
        setValues((prevValues: any) => {
          setFormValue(
            notifyForMessages,
            prevValues[notifyForMessages],
            element
          );
          setFormValue(notifyForStudy, prevValues[notifyForStudy], element);
          return prevValues;
        });
      } else {
        setIsPhoneNumberValid(false);
      }
    },
    [postJson, selectedCountryInfo, setFormValue]
  );

  // initial phone number check
  useEffect(() => {
    if (values[key] && selectedCountryInfo) {
      if (!initPhone) {
        const textWithDialCode = `${phoneNumberMask.dial_code}${values[key]}`;
        handleTwilioLookup(textWithDialCode);
      }
      setInitPhone(true);
    }
  }, [
    handleTwilioLookup,
    initPhone,
    values,
    selectedCountryInfo,
    phoneNumberMask
  ]);

  const handlePhoneNumberMask = useCallback(
    (text: string, countryPhonePrefix?: string) => {
      let lengthMatched = 0;
      internationalPhoneMask.forEach(data => {
        if (text.includes(data.dial_code)) {
          if (data.mask && data.dial_code.length >= lengthMatched) {
            lengthMatched = data.dial_code.length;
            setValues((prevValues: any) => ({
              ...prevValues,
              [key]: text.replace(data.dial_code, '')
            }));
            setFormValue(key, text.replace(data.dial_code, '') as any, element);
            setPhoneNumberMask(data);
            const internationalPhoneNumber = internationalPhoneMask.find(
              o => o.code === values[phoneNumberCode]
            );
            setSelectedCountryInfo(
              countryPhonePrefix
                ? countryPhonePrefix
                : internationalPhoneNumber
                  ? `${internationalPhoneNumber.code} ${internationalPhoneNumber.name} ${internationalPhoneNumber.dial_code}`
                  : `${data.code} ${data.name} ${data.dial_code}`
            );
          }
        }
      });
    },
    []
  );

  const initialValueRef = useRef(values[key]);
  useEffect(() => {
    if (initialValueRef.current) {
      handlePhoneNumberMask(initialValueRef.current);
    } else {
      setSelectedCountryInfo('US United States +1');
    }
  }, [handlePhoneNumberMask]);

  const formattedCountryCode = useMemo(() => {
    const countryCodeValue = selectedCountryInfo.split(' ')[0];
    const dialCodeValue = selectedCountryInfo.split(' ').slice(-1)[0];
    const country = internationalPhoneMask.find(
      o => o.dial_code === dialCodeValue && o.code === countryCodeValue
    );
    return country ? `${country.code} ${country.dial_code}` : '';
  }, [selectedCountryInfo, internationalPhoneMask]);

  const formattedMaskedCountryNumber = useMemo(() => {
    const { mask, dial_code } = phoneNumberMask;
    const maskWithoutDialCode = mask.replace(`${dial_code} `, '');
    return maskWithoutDialCode === mask
      ? mask.replace(/.*\)\s*/, '')
      : maskWithoutDialCode;
  }, [phoneNumberMask]);

  return (
    <FormInputView>
      <FormElementQuestion element={element} />
      <StyledTextInputView error={Boolean(phoneValidationError || twilioError)}>
        <StyledTouchableHighlight
          variant={variant}
          ref={(touchableHighlight: typeof TouchableHighlight) => {
            touchableHighlightRef.current = touchableHighlight;
          }}
          underlayColor={Colors.primary + '11'} // add alpha hex
          onPress={() => {
            (touchableHighlightRef.current as any).measureInWindow(
              (x: number, y: number, width: number, height: number) => {
                setOpenMenu(true);
              }
            );
          }}
        >
          <View>
            <StyledText
              value={selectedCountryInfo}
              numberOfLines={1}
              variant={variant}
              empty={true}
            >
              {formattedCountryCode}
            </StyledText>
            <StyledDropDownIcon variant={variant}>
              {variant === 'survey' ? (
                <Feather name='chevron-down' size={40} color='#fff' />
              ) : variant === 'inline' ? (
                <Feather
                  name='chevron-down'
                  size={18}
                  color={Colors.primaryPurple}
                />
              ) : (
                <Feather name='chevron-down' size={24} color={Colors.primary} />
              )}
            </StyledDropDownIcon>
          </View>
        </StyledTouchableHighlight>
        <StyledTextInput
          type='custom'
          keyboardType={'phone-pad'}
          options={{
            mask: formattedMaskedCountryNumber
          }}
          blurOnSubmit={false}
          placeholderTextColor={Colors.placeholderColor}
          refInput={(textInput: TextInput) => {
            textInputRef.current = textInput;
          }}
          {...props}
          minHeight={height}
          variant={variant}
          value={values[key]}
          empty={!values[key]}
          returnKeyType={returnKeyType}
          onChangeText={text => {
            // if blank input clear and return
            if (!text.replace(/\D/g, '')) {
              setFormValue(key, '' as any, element);
              setValues((prevValues: any) => ({
                ...prevValues,
                [key]: ''
              }));
              return;
            }
            text = text.trim().replace(/[^0-9+]/g, '');
            const textWithDialCode = `${phoneNumberMask.dial_code}${text}`;
            if (
              !phoneNumberMask.min &&
              textWithDialCode.length ===
                phoneNumberMask.mask.trim().replace(/[^0-9+]/g, '').length
            ) {
              handleTwilioLookup(textWithDialCode);
            } else if (
              phoneNumberMask.min &&
              textWithDialCode.length >= phoneNumberMask.min + 1
            ) {
              handleTwilioLookup(textWithDialCode);
            }
            onChangeText(text);
            setValues((prevValues: any) => ({
              ...prevValues,
              [key]: text
            }));
            setTouched(true);
          }}
          onBlur={() => {
            if (values[key] && values[key].length > 5) {
              const textWithDialCode = `${phoneNumberMask.dial_code}${values[key]}`;
              handleTwilioLookup(textWithDialCode);
            }
          }}
          onContentSizeChange={event => {
            setHeight(event.nativeEvent.contentSize.height);
          }}
          onSubmitEditing={() => {
            if (dismissKeyboard) {
              Keyboard.dismiss();
            } else if (submitOnReturn) {
              //   submitForm();
              Keyboard.dismiss();
            }
          }}
        />
        <StyledValidateView>
          {loading ? (
            <ActivityIndicator size='small' />
          ) : values[key] ? (
            <Feather
              name={twilioError ? 'alert-triangle' : 'check'}
              size={24}
            />
          ) : (
            <></>
          )}
        </StyledValidateView>
        <Modal visible={openMenu} transparent={true} animationType='fade'>
          <StyledModalView>
            <StyledModalBackgroundContainer
              onPress={() => {
                setOpenMenu(false);
              }}
            >
              <StyledModalBackground />
            </StyledModalBackgroundContainer>
            <StyledPickerView>
              <StyledCountryPickerView>
                {Platform.OS === 'web' ? (
                  <SelectField
                    value={countryNumber || selectedCountryInfo}
                    options={internationalPhoneMask
                      .sort((a, b) => {
                        if (a.code === 'US') return -1;
                        if (b.code === 'US') return 1;
                        return a.code.localeCompare(b.code);
                      })
                      .map(
                        country =>
                          `${country.code} ${country.name} ${country.dial_code}`
                      )}
                    onChange={option => {
                      setCountryNumber(String((option as SelectOption).value));
                    }}
                  />
                ) : (
                  <FlatListPicker
                    ellipsizeMode='tail'
                    onValueChanged={data => {
                      setCountryNumber(data);
                    }}
                    initialValue={selectedCountryInfo}
                    options={internationalPhoneMask
                      .sort((a, b) => {
                        if (a.code === 'US') return -1;
                        if (b.code === 'US') return 1;
                        return a.code.localeCompare(b.code);
                      })
                      .map(
                        country =>
                          `${country.code} ${country.name} ${country.dial_code}`
                      )}
                  />
                )}
              </StyledCountryPickerView>
              <StyledModalButtonView>
                <StyledModalButton
                  onPress={() => {
                    setOpenMenu(false);
                  }}
                >
                  <B color='dark'>Cancel</B>
                </StyledModalButton>
                <StyledModalButton
                  onPress={() => {
                    const dialCode = countryNumber.split(' ').slice(-1)[0];
                    setOpenMenu(false);
                    handlePhoneNumberMask(dialCode, countryNumber);
                  }}
                >
                  <B>Confirm</B>
                </StyledModalButton>
              </StyledModalButtonView>
            </StyledPickerView>
          </StyledModalView>
        </Modal>
      </StyledTextInputView>

      {twilioError && (
        <ErrorText errorTextColor={errorTextColor}>
          {String(twilioError || phoneValidationError?.message || 'error')}
        </ErrorText>
      )}

      <StyledRowView>
        <View>
          <StyledCheckboxContainer disabled={disabled}>
            <StyledBox
              disabled={!isPhoneNumberValid || !values[key]}
              touched={values[notifyForMessages]}
              activeOpacity={0.9}
              onPress={() => {
                setValues((prevValues: any) => ({
                  ...prevValues,
                  [notifyForMessages]: !Boolean(values[notifyForMessages])
                }));
                const textWithDialCode = `${phoneNumberMask.dial_code}${values[key]}`;
                handleTwilioLookup(textWithDialCode);
              }}
            >
              {values[notifyForMessages] && (
                <Feather name='check' size={14} color='#FFF' />
              )}
            </StyledBox>
            <StyledTextContainer>
              <StyledCaption color='dark'>Text for messages</StyledCaption>
            </StyledTextContainer>
          </StyledCheckboxContainer>
          <Spacer size='small' />
        </View>
        <View>
          <StyledCheckboxContainer disabled={disabled}>
            <StyledBox
              disabled={!isPhoneNumberValid || !values[key]}
              touched={values[notifyForStudy]}
              activeOpacity={0.9}
              onPress={() => {
                setValues((prevValues: any) => ({
                  ...prevValues,
                  [notifyForStudy]: !Boolean(values[notifyForStudy])
                }));
                const textWithDialCode = `${phoneNumberMask.dial_code}${values[key]}`;
                handleTwilioLookup(textWithDialCode);
              }}
            >
              {values[notifyForStudy] && (
                <Feather name='check' size={14} color='#FFF' />
              )}
            </StyledBox>
            <StyledTextContainer>
              <StyledCaption color='dark'>
                Text for study notifications
              </StyledCaption>
            </StyledTextContainer>
          </StyledCheckboxContainer>
          <Spacer size='small' />
        </View>
      </StyledRowView>
    </FormInputView>
  );
};

export const FormPhoneNumberPlugin: FormElementPlugin<
  any,
  FormPhoneNumberConfig
> = {
  name: 'Phone Number',
  type: 'PhoneNumber',
  element: FormPhoneNumber as React.FC<
    FormElementProps<any, FormPhoneNumberConfig>
  >
};
